import React from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ReplayIcon from '@mui/icons-material/Replay';
import dayjs from 'dayjs';

const STATUS_LABELS = {
  REQUESTED: '요청',
  ACCEPTED: '수락',
  REJECTED: '반려',
  CANCELED: '취소',
  CONFIRMED: '확정',
};

const STATUS_COLORS = {
  REQUESTED: 'warning',
  ACCEPTED: 'info',
  REJECTED: 'error',
  CANCELED: 'default',
  CONFIRMED: 'success',
};

const getYmOptions = () => {
  const options = [];
  const now = dayjs();
  for (let y = now.year() + 1; y >= now.year() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      options.push(`${y}${String(m).padStart(2, '0')}`);
    }
  }
  return options;
};

const formatDtm = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM-DD HH:mm') : '-';
};

const WorkScheduleRequestListTab = ({
  list,
  loading,
  searchYm,
  mode = 'my',
  onSearchYmChange,
  onCancel,
  onConfirm,
  onReject,
}) => {
  const isInbox = mode === 'inbox';

  const columns = [
    { field: 'reqNo', headerName: '요청번호', minWidth: 170, flex: 0.8 },
    ...(isInbox ? [
      { field: 'reqUsrNm', headerName: '요청자', minWidth: 100, flex: 0.5 },
      { field: 'reqDptNm', headerName: '부서', minWidth: 120, flex: 0.6 },
    ] : []),
    {
      field: 'reqWorkDt',
      headerName: '근무일',
      minWidth: 120,
      flex: 0.5,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      field: 'currentShiftNm',
      headerName: '현재 근무',
      minWidth: 160,
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box>
          <Box>{row.currentShiftNm || '-'}</Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {formatDtm(row.currentStartDtm)} ~ {formatDtm(row.currentEndDtm)}
          </Box>
        </Box>
      ),
    },
    {
      field: 'reqShiftNm',
      headerName: '변경 근무',
      minWidth: 160,
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box>
          <Box>{row.reqShiftNm || '-'}</Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {formatDtm(row.reqStartDtm)} ~ {formatDtm(row.reqEndDtm)}
          </Box>
        </Box>
      ),
    },
    { field: 'reason', headerName: '사유', minWidth: 220, flex: 1.1 },
    {
      field: 'reqStatus',
      headerName: '상태',
      minWidth: 100,
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={STATUS_LABELS[value] || value}
          color={STATUS_COLORS[value] || 'default'}
        />
      ),
    },
    {
      field: 'crtDt',
      headerName: '요청일',
      minWidth: 150,
      flex: 0.7,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: isInbox ? 170 : 90,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.reqStatus !== 'REQUESTED') return null;
        if (isInbox) {
          return (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => onConfirm(row)}
              >
                확정
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<HighlightOffIcon />}
                onClick={() => onReject(row)}
              >
                반려
              </Button>
            </Stack>
          );
        }
        return (
          <Tooltip title="요청 취소">
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={() => onCancel(row)}
            >
              취소
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="기준월"
          value={searchYm}
          onChange={(event) => onSearchYmChange(event.target.value)}
          sx={{ width: 140 }}
        >
          {getYmOptions().map((ym) => (
            <MenuItem key={ym} value={ym}>
              {ym.slice(0, 4)}년 {parseInt(ym.slice(4), 10)}월
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.reqId}
        loading={loading}
        autoHeight
        density="compact"
        rowHeight={58}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </Box>
  );
};

export default WorkScheduleRequestListTab;
